import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, CreditCard, Lock, CheckCircle } from 'lucide-react';
import { getValidatedCartItems, clearCartStorage } from '@/utils/cartStorage';
import { toast } from 'react-hot-toast';

// Stripe instance will be initialized per store
let stripeInstance: Promise<Stripe | null> | null = null;

interface OrderData {
  cart: any[];
  total: number;
  orderTotal: number;
  shippingTotal: number;
  vatAmount: number;
  customerName: string;
  customerEmail: string;
  storeInfo: any;
  paymentIntentId: string;
}

interface PaymentFormProps {
  clientSecret: string;
  orderData: OrderData;
  onPaymentSuccess: (paymentIntent: any) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ 
  clientSecret, 
  orderData, 
  onPaymentSuccess 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const handlePaymentSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setPaymentError('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError('Card information not found. Please refresh and try again.');
      setIsProcessing(false);
      return;
    }

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: orderData.customerName,
            email: orderData.customerEmail,
          },
        }
      });

      if (error) {
        setPaymentError(error.message || 'Payment failed. Please try again.');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaymentSuccess(paymentIntent);
      } else {
        setPaymentError('Payment was not completed. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError('An unexpected error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePaymentSubmit} className="space-y-6">
      <div className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: 'var(--stripe-text-color, #374151)',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                '::placeholder': {
                  color: 'var(--stripe-placeholder-color, #9CA3AF)',
                },
                backgroundColor: 'transparent',
                iconColor: 'var(--stripe-icon-color, #6B7280)',
              },
              invalid: {
                color: '#EF4444',
                iconColor: '#EF4444',
              },
              complete: {
                color: '#10B981',
                iconColor: '#10B981',
              },
            },
            hidePostalCode: false,
          }}
        />
      </div>
      
      {paymentError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Payment Failed
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{paymentError}</p>
              <div className="mt-3 flex space-x-3">
                <button
                  type="button"
                  onClick={() => setPaymentError(null)}
                  className="text-sm font-medium text-red-800 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100 underline"
                >
                  Try Again
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="text-sm font-medium text-red-800 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100 underline"
                >
                  Return to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full h-12 text-lg font-semibold"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing Payment...
          </>
        ) : (
          <>
            <Lock className="mr-2 h-5 w-5" />
            Pay ${orderData.total.toFixed(2)}
          </>
        )}
      </Button>
    </form>
  );
};

export const NewStripePaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientSecret = searchParams.get('client_secret');
  
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        // Get order data from localStorage
        const savedOrderData = localStorage.getItem('pendingStripeOrder');
        if (!savedOrderData) {
          throw new Error('Order data not found. Please start checkout again.');
        }

        const parsedOrderData = JSON.parse(savedOrderData) as OrderData;
        
        // Get cart items with comprehensive validation and fallback handling
        const { items: cartItems, error: cartError } = getValidatedCartItems();
        
        if (cartError || !cartItems) {
          throw new Error(cartError || 'Unable to load cart items. Please return to cart and try again.');
        }
        
        // Validate required data
        if (!parsedOrderData.storeInfo?.StripeKey) {
          throw new Error('Stripe is not configured for this store.');
        }

        // Update the order data with the cart items
        parsedOrderData.cart = cartItems;

        // Initialize Stripe
        stripeInstance = loadStripe(parsedOrderData.storeInfo.StripeKey);
        
        setOrderData(parsedOrderData);
        setIsLoading(false);

      } catch (err) {
        console.error('Payment initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize payment');
        setIsLoading(false);
      }
    };

    // Check for client secret
    if (!clientSecret) {
      setError('Payment session not found. Please start checkout again.');
      setIsLoading(false);
      return;
    }

    initializePayment();
  }, [clientSecret]);

  const handlePaymentSuccess = async (paymentIntent: any) => {
    try {
      // Get cart items from localStorage (more reliable than orderData)
      const { items: cartItems, error: cartError } = getValidatedCartItems();
      
      if (cartError || !cartItems) {
        throw new Error(cartError || 'Cart items not found in localStorage');
      }

      if (!orderData) {
        throw new Error('Order data not available');
      }

      // Prepare order payload with cart items from localStorage
      const orderPayload = {
        domain: window.location.hostname,
        items: cartItems.map((cartItem: any) => ({
          product_id: cartItem.product_id,
          quantity: cartItem.quantity,
          variation_id: cartItem.variation_id || null,
          product_name: cartItem.name || cartItem.product_name || 'Product',
          price: cartItem.price || 0,
          total: (cartItem.price || 0) * (cartItem.quantity || 1)
        })),
        customer_name: orderData.customerName,
        customer_email: orderData.customerEmail,
        customer_phone: '',
        shipping_address: {
          address: '',
          city: '',
          state: '',
          country: ''
        },
        billing_address: {
          address: '',
          city: '',
          state: '',
          country: ''
        },
        notes: '',
        shipping_method: 'standard',
        payment_method: 'stripe',
        payment_reference: paymentIntent.id,
        payment_status: 'completed',
        amount: orderData.total,
        currency: 'USD'
      };

      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      const result = await response.json();

      if (result.status && result.orderID) {
        // Clear all cart-related data from localStorage
      clearCartStorage();
        
        // Show success message
        toast.success('Payment successful! Redirecting to order confirmation...');
        
        // Navigate to order complete page
        navigate('/order-complete', {
          state: {
            orderId: result.orderID,
            trackingId: result.trackingId
          }
        });
      } else {
        throw new Error(result.message || 'Failed to create order');
      }
    } catch (err) {
      console.error('Order creation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      
      toast.error(`Payment successful but order creation failed: ${errorMessage}`);
      
      // Don't clear cart on error - user may need to retry or contact support
      // Only clear the pending order to avoid confusion
      localStorage.removeItem('pendingStripeOrder');
      navigate('/order-complete');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="p-8">
              <div className="text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
                <p className="mt-4 text-gray-600 dark:text-gray-400">
                  Initializing secure payment...
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="p-8">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                  <CreditCard className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Payment Error
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <Button 
                  onClick={() => navigate('/cart')}
                  className="w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Return to Cart
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main payment form
  if (!orderData || !stripeInstance) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-green-600" />
              Secure Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Summary */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">
                Order Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Items:</span>
                  <span className="font-medium">{orderData.cart.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                  <span className="font-medium">${orderData.orderTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Shipping:</span>
                  <span className="font-medium">${orderData.shippingTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">VAT:</span>
                  <span className="font-medium">${orderData.vatAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg border-t pt-2 mt-2">
                  <span>Total:</span>
                  <span>${orderData.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Payment Form */}
            <Elements stripe={stripeInstance}>
              <PaymentForm
                clientSecret={clientSecret}
                orderData={orderData}
                onPaymentSuccess={handlePaymentSuccess}
              />
            </Elements>

            {/* Security Notice */}
            <div className="text-center text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Lock className="h-3 w-3" />
                <span>Secured by Stripe</span>
              </div>
              <p>Your payment information is encrypted and secure</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


